import { StoryConfig } from "game/gameUI/UiHandlers/StoriesHandler/types";
import { TicketsCounter } from "../TicketsCounter";
import './styles.css';
import { translate } from "game/translate";
import { useLocalStorage } from "ui/hooks/useLocalStorage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showImagePreviewModal } from "ui/store/actions/gameUIActions";
import { useDispatch } from "react-redux";
import { GAEvent, GAEventNames } from "game/analytics/events";

interface StoryViewProps {
    onClose: () => void;
    onClosePanel: () => void;
    story: StoryData;
}

export type StoryData = StoryConfig & { storyNumber: number; };

export const StoryView = ({ onClose, onClosePanel, story }: StoryViewProps) => {
  const dispatch = useDispatch();
  const [storyProgress, setStoryProgress] = useLocalStorage(`stories_story_${story.storyNumber}_progress`, 0);
  const [tickets, setTickets] = useLocalStorage('stories_tickets_counter', 0);

  const ref = useRef<HTMLDivElement>(null);
  
  const [isTextCollapsed, setIsTextCollapsed] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const container = ref.current;
      if (!container) return;

      const targetIndex = Math.max(0, Math.min(Number(storyProgress) - 1, story.totalImages - 1));
      const targetEl = container.querySelector(`.story-image-container[data-image-index="${targetIndex + 1}"]`) as HTMLElement | null;
      if (targetEl) {
        const prevSnap = (container.style as any).scrollSnapType;
        (container.style as any).scrollSnapType = 'none';

        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const desiredTop = targetRect.top - containerRect.top + container.scrollTop;
        const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
        container.scrollTop = Math.min(Math.max(0, desiredTop), maxScrollTop);

        setTimeout(() => {
          (container.style as any).scrollSnapType = prevSnap || '';
        }, 0);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);
  
  const handleUnlockStory = useCallback((price: number, imageIndex: number) => {

    GAEvent(GAEventNames.STORIES_UNLOCK_IMAGE, {
      story_name: story.id,
      image_number: imageIndex,
    });


    if (tickets >= price) {
      const prevProgress = storyProgress;
      setTickets(tickets - price);
      setStoryProgress(storyProgress + 1);
      handleCompleteAllStories(prevProgress, storyProgress + 1);
    } else {
      GAEvent(GAEventNames.STORIES_NO_TICKET_BANNER_VIEW, {
        story_name: story.id,
        image_number: imageIndex,
      });
      (window as any).currentStoryNumber = story.storyNumber;
      (window as any).currentNextStoryImageNumber = imageIndex;
      (window as any).openModal("stories_no_tickets");

      const noTicketsModal = document.getElementById("stories_no_tickets");
      const closeButton = noTicketsModal?.querySelector(".close_btn");

      const closeHandler = () => {
        GAEvent(GAEventNames.STORIES_NO_TICKET_BANNER_CLOSE, {
          story_name: story.id,
          image_number: imageIndex,
        });
        closeButton?.removeEventListener("click", closeHandler);
      };

      if (closeButton) {
        closeButton.removeEventListener("click", closeHandler);
        closeButton.addEventListener("click", closeHandler);
      }
    }
  }, [tickets, storyProgress]);

  const prices = useMemo(() => {
    return story.prices || Array.from({ length: story.totalImages }, () => 1);
  }, [story.prices]);

  const allUnlockPrice = useMemo(() => {
    return prices.slice(storyProgress).reduce((acc, price) => acc + price, 0);
  }, [story.prices, prices, storyProgress]);

  const imagesToComplete = useMemo(() => {
    return prices.slice(storyProgress).length;
  }, [story.prices, prices, storyProgress]);

  const handleUnlockAll = useCallback(() => {
    GAEvent(GAEventNames.STORIES_UNLOCK_ALL, {
      story_name: story.id,
    });
    const prevProgress = storyProgress;
    setTickets(tickets - allUnlockPrice);
    setStoryProgress(story.totalImages);
    handleCompleteAllStories(prevProgress, story.totalImages);
  }, [tickets, allUnlockPrice, storyProgress, story.totalImages]);

  const handleCompleteAllStories = useCallback((prevProgress: number, newProgress: number) => {
    if (prevProgress != newProgress && newProgress === story.totalImages) {
      
      const congratulationsModal = document.getElementById(
        "stories_congratulations"
      );
      if (congratulationsModal) {
        GAEvent(GAEventNames.STORIES_CONGRATULATIONS_BANNER_VIEW, {
          story_name: story.id,
        });

        const watchListener = () => {
          GAEvent(GAEventNames.STORIES_CONGRATULATIONS_BANNER_PLAY_VIDEO, {
            story_name: story.id,
          });
        }

        const watchVideoBtn = document.querySelector(".watch-video-button");
        if (watchVideoBtn) {
          watchVideoBtn.removeEventListener("click", watchListener);
          watchVideoBtn.addEventListener("click", watchListener);
        }

        congratulationsModal.dataset.storyNumber = story.storyNumber.toString();
        const modalImg = congratulationsModal.querySelector(".modals_area_img");
        if (modalImg) {
          (modalImg as HTMLImageElement).src = `/assets/stories/${story.id}/images/win-cover.webp`;
        }
        (window as any).storyId = story.id;
        (window as any).openModal("stories_congratulations");
      }
    }
  }, [tickets, allUnlockPrice, story.totalImages]);

  const previewText = useMemo(() => translate('stories_preview_text'), []);
  
  const handleOpenImagePreviewModal = useCallback((index: number, isLocked: boolean) => {
    if (isLocked) {
      return;
    }

    GAEvent(GAEventNames.STORIES_PREVIEW_IMAGE, {
      story_name: story.id,
      image_number: index + 1,
    });

    const images = Array.from({ length: story.totalImages }, (_, idx) => ({
      src: `/assets/stories/${story.id}/images/image-${idx + 1}.webp`,
      isUnlocked: idx + 1 <= (Number(storyProgress) || 0),
      alt: `Story image ${idx + 1}`,
      previewText: story.previewText?.[idx]
    }));

    dispatch(showImagePreviewModal({
      id: story.storyNumber,
      storyName: story.id,
      images,
      initialSlideIndex: index,
    }));
  }, [story.id, story.storyNumber, storyProgress]);

  const handleOpenVideoPreviewModal = useCallback(() => {
    GAEvent(GAEventNames.STORIES_VIDEO_BUTTON, {
      story_name: story.id,
    });
    dispatch(showImagePreviewModal({
      id: story.storyNumber,
      storyName: story.id,
      placement: 'bottom_sticky_button',
      images: [{ src: `/assets/stories/${story.id}/images/video`, isUnlocked: true, alt: 'Story video' }],
      initialSlideIndex: 0,
    }));
  }, [story.id, story.storyNumber]);

  const handleTextCollapseExpand = useCallback(() => {
    const newValue = !isTextCollapsed;
    if (!newValue) {
        GAEvent(GAEventNames.STORIES_SHOW_MORE, {
          story_name: story.id,
        });
    } else {
      GAEvent(GAEventNames.STORIES_SHOW_LESS, {
        story_name: story.id,
      });
    }
    setIsTextCollapsed(newValue);
  }, [story.id, isTextCollapsed]);

  return (
      <div 
      className={`d-flex text-white flex-column h-100 w-100`}
    >
      <div className="story-content">
          <div id="closing-ignore-el" className="info-panel-back-btn-story" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="18" viewBox="0 0 11 18" fill="none">
            <path d="M9 16L2 9L9 2" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          </div>
          <TicketsCounter className="story-ticket-counter--story-view" />
          <div className="d-flex justify-content-end align-items-center story-close-btn-wrapper">
              <button 
                  className="btn btn-link text-white p-0 border-0 close-icon"
                  onClick={onClosePanel}
              >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                  </svg>
              </button>
          </div>

          <div className="story-title d-flex align-items-center w-100">
            <span className="story-title-text">{translate(story.titleKey)}</span>
            <span className="story-progress-text">{`${storyProgress}/${story.totalImages}`}</span>
          </div>

          <div className={`story-description ${isTextCollapsed ? 'd-flex' : 'd-block'} `}>
            <div className={`flex-grow-1 story-description-text ${isTextCollapsed ? 'story-description-text--colapsed' : 'story-description-text--expanded'}`}>
              {translate(story.descriptionKey)}
            </div>
            <div className="flex-grow-0 story-show-more-text" onClick={handleTextCollapseExpand}>{isTextCollapsed ? translate('stories_show_more') : translate('stories_show_less')}</div>
          </div>

          <div ref={ref} className="story-images-wrapper stories-scroll">
            <div className={`story-images${(tickets >= allUnlockPrice && storyProgress < story.totalImages) ? ' story-images--extended' : ''}`}>
              {Array.from({ length: story.totalImages }, (_, idx) => {
                    const imageIndex = idx + 1;
                    const isLocked = imageIndex > (Number(storyProgress) || 0);
                    const isHaveOpenBtn = idx == storyProgress;

                    const price = prices[idx] || 1;

                    return (
                      <div id="closing-ignore-el" onClick={() => handleOpenImagePreviewModal(idx, isLocked)} key={imageIndex} className="story-image-container-wrapper">
                        <div
                          className={`story-image-container${isLocked ? ' locked' : ''}`}
                          data-image-index={idx}
                          data-preview-text={previewText}
                        >
                          <img
                            src={`/assets/stories/${story.id}/images/image-${imageIndex}.webp`}
                            className="story-image-container-image"
                            alt={`Story image ${imageIndex}`}
                          />


                          {isLocked && !isHaveOpenBtn && <div className="story-image-container-price">
                            <img src="/assets/stories/ticket.webp" alt="Tickets" style={{ width: '24px' }}/>
                            <span>{price}</span>
                          </div>}

                          {!isLocked && story.hasText && (
                            <div className="text-icon" role="button" aria-label="Text">
                              <div className="text-icon-bg">
                              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <path d="M5 26V6C5 4.93913 5.42143 3.92172 6.17157 3.17157C6.92172 2.42143 7.93913 2 9 2H23C24.0609 2 25.0783 2.42143 25.8284 3.17157C26.5786 3.92172 27 4.93913 27 6V24.25C27 24.7141 26.8156 24.5796 26.4874 24.7437C26.1592 24.9078 25.7141 25 25.25 25H7C7 25.2652 7.21071 25.5196 7.58579 25.7071C7.96086 25.8946 8.46957 26 9 26H26C26.2652 26 26.5196 26.1054 26.7071 26.2929C26.8946 26.4804 27 26.7348 27 27C27 27.2652 26.8946 27.5196 26.7071 27.7071C26.5196 27.8946 26.2652 28 26 28H9C7.93913 28 6.92172 27.7893 6.17157 27.4142C5.42143 27.0391 5 26.5304 5 26ZM10.75 6C10.2859 6 9.84075 6.18437 9.51256 6.51256C9.18437 6.84075 9 7.28587 9 7.75V9.25C9 10.216 9.784 11 10.75 11H21.25C21.7141 11 22.1592 10.8156 22.4874 10.4874C22.8156 10.1592 23 9.71413 23 9.25V7.75C23 7.28587 22.8156 6.84075 22.4874 6.51256C22.1592 6.18437 21.7141 6 21.25 6H10.75Z" fill="white"></path>
                              </svg>
                              </div>
                            </div>
                          )}

                          {isLocked && (
                            <div className="story-image-overlay">
                              <div className="lock-content">
                              
                                <div className="lock-icon-wrapper">
                                  <img src="/assets/stories/lock.webp" alt="Locked" className="lock-icon" />
                                </div>
                                {isHaveOpenBtn && (
                                  <div id="closing-ignore-el" className="story-btn" onClick={() => handleUnlockStory(price, imageIndex)}>
                                    <span>{translate('stories_story_unlock_button')}</span>
                                    <div className="story-btn-divider"/>
                                    <div className="story-btn-icon">
                                      <img src="/assets/stories/ticket.webp" alt="Tickets" style={{ width: '24px' }}/>
                                      <span>
                                        {price}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
            {tickets >= allUnlockPrice && storyProgress < story.totalImages && <div id="closing-ignore-el" onClick={handleUnlockAll} className="story-sticky-btn story-unlock-all-btn">
              <img src="/assets/stories/ticket.webp" alt="Tickets" style={{ width: '34px' }}/>
              <span>{`${translate('stories_unlock_all_text')} (${imagesToComplete})`}</span>
            </div>}
            {storyProgress == story.totalImages && (
              <div id="closing-ignore-el" className="story-btn story-sticky-btn story-btn-video" style={{fontSize: '16px'}} onClick={handleOpenVideoPreviewModal}>
                <span>{translate('stories_play_video')}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <g filter="url(#filter0_d_1131_1304)">
                  <path d="M3.75 4.39754C3.75 3.46832 4.72789 2.86395 5.55902 3.27951L17.7639 9.38197C18.6852 9.84262 18.6852 11.1574 17.7639 11.618L5.55902 17.7205C4.72789 18.1361 3.75 17.5317 3.75 16.6025V4.39754Z" fill="white"/>
                  </g>
                  <defs>
                  <filter id="filter0_d_1131_1304" x="3.75" y="3.146" width="14.7051" height="15.3728" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dy="0.664795"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1131_1304"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1131_1304" result="shape"/>
                  </filter>
                  </defs>
                </svg>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};